/**
 * Notification Hub - Main Container Component
 *
 * Central hub for notification management with tabbed interface:
 * - Event Definitions (merchant-specific)
 * - Templates (merchant-specific)
 * - Languages (global)
 * - Send Notification
 *
 * Access control:
 * - Super Admin: Can manage notifications for all merchants (shows merchant selector)
 * - Merchant users (Notification Manager, etc.): Can only manage their own merchant's notifications
 */

import React, { useEffect, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Bell, Building2 } from "lucide-react";
import { AppCard, PageLayout } from "../../components/ui";
import { createTabHeaderTemplate } from "../../components/ui/tabHeaderTemplate";
import { storageService } from "../../services/StorageService";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { canManageAllMerchantNotifications, isSuperAdmin } from "../../authGuard";
import { SelectItem } from "../../models/SelectItem";
import EventDefinitions from "./EventDefinitions";
import Templates from "./Templates";
import Languages from "./Languages";
import SendNotification from "./SendNotification";
import Topics from "./Topics";
import FirebaseConfig from "./FirebaseConfig";

interface NotificationHubProps {
    merchantId?: number;
    initialTab?: number;
}

/**
 * Tab indices:
 * 0 = Event Definitions
 * 1 = Templates
 * 2 = Languages
 * 3 = Send Notification
 * 4 = Topics
 * 5 = Firebase Config (Super Admin only)
 */
const NotificationHub: React.FC<NotificationHubProps> = ({
    merchantId: propMerchantId,
    initialTab = 0,
}) => {
    // Check if user can manage notifications for all merchants (super admin)
    const canManageAllMerchants = canManageAllMerchantNotifications();

    // For merchant users, use their logged-in merchant ID directly
    const loggedInMerchantId = storageService.getLoggedInUserMerchantId();

    // Get merchant ID from props, storage, or logged-in user's merchant
    const storedMerchantId = storageService.getSelectedMerchantId();
    const getInitialMerchantId = (): number | null => {
        if (propMerchantId) return propMerchantId;
        if (!canManageAllMerchants && loggedInMerchantId) return loggedInMerchantId;
        if (storedMerchantId) return Number(storedMerchantId);
        return null;
    };

    const [activeIndex, setActiveIndex] = useState(initialTab);
    const [selectedEventCode, setSelectedEventCode] = useState<string | undefined>();

    // Merchant selection state
    const [merchants, setMerchants] = useState<SelectItem<number>[]>([]);
    const [selectedMerchantId, setSelectedMerchantId] = useState<number | null>(getInitialMerchantId());
    const [isLoadingMerchants, setIsLoadingMerchants] = useState(false);

    // Determine if we need to show merchant selector (only for super admin without selection)
    const needsMerchantSelection = canManageAllMerchants && (selectedMerchantId === null || selectedMerchantId === 0);

    useEffect(() => {
        // Only load merchant list if user can manage all merchants
        if (canManageAllMerchants) {
            loadMerchants();
        }
    }, []);

    const loadMerchants = async () => {
        setIsLoadingMerchants(true);
        const result = await merchantApiService.getSelectItem();
        if (result.success && result.data) {
            // Convert to SelectItem<number> format
            const merchantOptions: SelectItem<number>[] = result.data.map((m) => ({
                label: m.label,
                value: typeof m.value === 'string' ? parseInt(m.value, 10) : m.value as number,
            }));
            setMerchants(merchantOptions);

            // Auto-select first merchant if none selected and only one available
            if (needsMerchantSelection && merchantOptions.length === 1) {
                setSelectedMerchantId(merchantOptions[0].value);
            }
        }
        setIsLoadingMerchants(false);
    };

    // When an event is selected from EventDefinitions, switch to Templates tab filtered by that event
    const handleEventSelect = (eventCode: string) => {
        setSelectedEventCode(eventCode);
        setActiveIndex(1); // Switch to Templates tab
    };

    const handleMerchantChange = (merchantId: number) => {
        setSelectedMerchantId(merchantId);
        // Optionally store in storage for persistence
        storageService.setSelectedMerchantId(merchantId.toString());
    };

    const tabHeaderTemplate = createTabHeaderTemplate(activeIndex);

    // Merchant selector component
    const MerchantSelector = () => (
        <div className="p-4 border-b border-gray-200 bg-slate-50">
            <div className="flex items-center gap-4">
                <label className="font-semibold text-gray-700 flex items-center gap-2">
                    <Building2 size={16} />
                    Merchant:
                </label>
                <Dropdown
                    value={selectedMerchantId}
                    options={merchants}
                    onChange={(e) => handleMerchantChange(e.value)}
                    placeholder="Select a merchant"
                    className="w-80"
                    filter
                    filterPlaceholder="Search merchants..."
                    loading={isLoadingMerchants}
                    emptyMessage="No merchants found"
                />
                {selectedMerchantId && (
                    <span className="text-sm text-gray-500">
                        ID: {selectedMerchantId}
                    </span>
                )}
            </div>
        </div>
    );

    // Show warning if merchant selection required but not selected
    const MerchantRequiredWarning = () => (
        <div className="p-4">
            <Message
                severity="warn"
                text="Please select a merchant to manage event definitions, templates and send notifications."
                className="w-full"
            />
        </div>
    );

    return (
        <PageLayout heading="Notifications" icon={Bell}>
            <AppCard>
                <AppCard.Body className="!p-0">
                    {/* Only show merchant selector for super admins who can manage all merchants */}
                    {canManageAllMerchants && <MerchantSelector />}

                    <TabView
                        activeIndex={activeIndex}
                        onTabChange={(e) => {
                            setActiveIndex(e.index);
                            // Clear event filter when switching away from templates
                            if (e.index !== 1) {
                                setSelectedEventCode(undefined);
                            }
                        }}
                    >
                        <TabPanel
                            header="Event Definitions"
                            headerTemplate={tabHeaderTemplate}
                        >
                            {needsMerchantSelection ? (
                                <MerchantRequiredWarning />
                            ) : (
                                <EventDefinitions
                                    merchantId={selectedMerchantId!}
                                    onEventSelect={handleEventSelect}
                                />
                            )}
                        </TabPanel>

                        <TabPanel
                            header="Templates"
                            headerTemplate={tabHeaderTemplate}
                        >
                            {needsMerchantSelection ? (
                                <MerchantRequiredWarning />
                            ) : (
                                <Templates
                                    merchantId={selectedMerchantId!}
                                    initialEventCode={selectedEventCode}
                                />
                            )}
                        </TabPanel>

                        <TabPanel
                            header="Languages"
                            headerTemplate={tabHeaderTemplate}
                        >
                            <Languages />
                        </TabPanel>

                        <TabPanel
                            header="Send Notification"
                            headerTemplate={tabHeaderTemplate}
                        >
                            {needsMerchantSelection ? (
                                <MerchantRequiredWarning />
                            ) : (
                                <SendNotification merchantId={selectedMerchantId!} />
                            )}
                        </TabPanel>

                        <TabPanel
                            header="Topics"
                            headerTemplate={tabHeaderTemplate}
                        >
                            {needsMerchantSelection ? (
                                <MerchantRequiredWarning />
                            ) : (
                                <Topics merchantId={selectedMerchantId!} />
                            )}
                        </TabPanel>

                        {isSuperAdmin() && (
                            <TabPanel
                                header="Firebase Config"
                                headerTemplate={tabHeaderTemplate}
                            >
                                {needsMerchantSelection ? (
                                    <MerchantRequiredWarning />
                                ) : (
                                    <FirebaseConfig merchantId={selectedMerchantId!} />
                                )}
                            </TabPanel>
                        )}
                    </TabView>
                </AppCard.Body>
            </AppCard>
        </PageLayout>
    );
};

export default NotificationHub;
